/**
 * In-board element-library panel (Block 5.3 "insert into the open scene").
 * Browses the workspace library (folders → items) and inserts an item's
 * elements into the currently-open board as fresh copies.
 */

import { useCallback, useEffect, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { ryfeApi } from "../api";
import { insertLibraryElements } from "../scene-links";

import { LibraryPreview } from "./LibraryPreview";

import "./ryfe.scss";

import type { LibraryListing } from "../api";

export const LibraryInsertPanel = ({
  wsId,
  api,
  onClose,
}: {
  wsId: string;
  api: ExcalidrawImperativeAPI;
  onClose: () => void;
}) => {
  const [listing, setListing] = useState<LibraryListing | null>(null);
  const [stack, setStack] = useState<{ id: string | null; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inserting, setInserting] = useState<string | null>(null);
  // v1.3.0 Block 2 (D-17) — two views: list and preview grid (name below).
  const [view, setView] = useState<"list" | "grid">("list");
  const [saving, setSaving] = useState(false);

  const openFolder = useCallback(
    async (folderId: string | null, name: string, replaceDepth?: number) => {
      try {
        const l = await ryfeApi.getLibrary(wsId, folderId || undefined);
        setListing(l);
        setStack((s) => {
          if (replaceDepth !== undefined) {
            return s.slice(0, replaceDepth + 1);
          }
          return [...s, { id: folderId, name }];
        });
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [wsId],
  );

  useEffect(() => {
    openFolder(null, "Bibliothek").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * v1.3.0 Block 2 (D-17) — the new creation path: whatever is selected in the
   * open scene becomes an own library element. Files into the folder currently
   * shown when that sits inside "Meine Elemente"; otherwise the server default
   * (my_root) applies — the system tree never accepts own elements.
   */
  const saveSelection = async () => {
    const selected = api.getSceneElements().filter((el) => {
      const sel = api.getAppState().selectedElementIds;
      return sel[el.id];
    });
    if (selected.length === 0) {
      setError("Nichts ausgewählt — erst Elemente in der Szene markieren.");
      return;
    }
    const name = window.prompt(
      "Name des Bibliothekselements:",
      `Auswahl (${selected.length} Elem.)`,
    );
    if (!name) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const target =
        listing && listing.id && listing.read_only === false
          ? listing.id
          : undefined;
      await ryfeApi.createLibraryItemFromSelection(
        wsId,
        name,
        selected as unknown[],
        target,
      );
      const cur = stack[stack.length - 1];
      await openFolder(
        cur?.id ?? null,
        cur?.name ?? "Bibliothek",
        stack.length - 1,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const insert = async (itemId: string) => {
    setInserting(itemId);
    setError(null);
    try {
      const item = await ryfeApi.getLibraryItem(wsId, itemId);
      insertLibraryElements(api, item.elements);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInserting(null);
    }
  };

  return (
    <aside className="ryfe-sidepanel">
      <div className="ryfe-sidepanel__head">
        <strong>Bibliothek</strong>
        <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
          ✕
        </button>
      </div>

      <nav className="ryfe-breadcrumb ryfe-breadcrumb--tight">
        {stack.map((c, i) => (
          <span key={`${c.id}-${i}`}>
            {i > 0 && " / "}
            <button
              className="ryfe-btn ryfe-btn--link"
              onClick={() => openFolder(c.id, c.name, i)}
            >
              {c.name}
            </button>
          </span>
        ))}
      </nav>

      <div className="ryfe-sidepanel__tools">
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={saveSelection}
          disabled={saving}
          title="Markierte Elemente als eigenes Bibliothekselement sichern"
        >
          ★ Auswahl sichern
        </button>
        <span className="ryfe-topbar__spacer" />
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => setView((v) => (v === "list" ? "grid" : "list"))}
          title="Ansicht wechseln"
        >
          {view === "list" ? "▦ Raster" : "☰ Liste"}
        </button>
      </div>

      {error && <div className="ryfe-auth__error">{error}</div>}

      <div
        className={
          view === "grid"
            ? "ryfe-sidepanel__body ryfe-libgrid"
            : "ryfe-sidepanel__body"
        }
      >
        {!listing ? (
          <div className="ryfe-muted">Loading…</div>
        ) : (
          <>
            {listing.folders.map((f) => (
              <button
                key={f.id}
                className="ryfe-picker__row"
                onClick={() => openFolder(f.id, f.name)}
              >
                📁 {f.name}{" "}
                <span className="ryfe-muted">({f.item_count ?? 0})</span>
              </button>
            ))}
            {listing.items.map((it) =>
              view === "grid" ? (
                // CC_05 Block 3 — das In-Board-Panel hatte im Raster gar keine
                // Vorschau (nur Name + Anzahl). Jetzt dasselbe Thumbnail wie auf
                // der /library-Seite, gerendert host-seitig aus den Elementen.
                <button
                  key={it.id}
                  className="ryfe-libcell"
                  onClick={() => insert(it.id)}
                  disabled={inserting === it.id}
                  title="In die Szene einfügen"
                >
                  <LibraryPreview wsId={wsId} itemId={it.id} />
                  <span className="ryfe-libcell__name">{it.name}</span>
                </button>
              ) : (
                <button
                  key={it.id}
                  className="ryfe-picker__row"
                  onClick={() => insert(it.id)}
                  disabled={inserting === it.id}
                  title="In die Szene einfügen"
                >
                  ▫ {it.name}{" "}
                  <span className="ryfe-muted">({it.element_count} Elem.)</span>
                </button>
              ),
            )}
            {listing.folders.length === 0 && listing.items.length === 0 && (
              <div className="ryfe-muted">Leer.</div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
